import { FeatureCollection } from '@turf/turf';
import * as Turf from '@turf/turf';
import { LngLatBounds, RasterTileSource } from 'maplibre-gl';
import { UUIDUtils } from '~/common/typescript/utils/UuidUtils';
import { Icon } from '~/components/common/IconSelector/svg/shapes';
import { IconUtil } from '~/utils/IconUtil';

const sourceId = 'highlight';
const fillLayerId = 'highlight-fill';
const lineLayerId = 'highlight-line';
const symbolLayerId = 'highlight-symbol';

// Store current highlight data for zoom updates
let currentHighlightData: {
  featureCollection: FeatureCollection;
  elementStyle?: { size?: number; frame?: string; strokeWidth?: number; opacity?: number };
} | null = null;

// Debounce zoom updates to prevent flickering
let zoomUpdateTimeout: NodeJS.Timeout | null = null;

const MapUtils = {
  clearHighlight: () => {
    const map = window.map;
    if (!map) {
      return;
    }

    const layers = map.getStyle()?.layers || [];
    layers.forEach((layer: any) => {
      if (layer.source === sourceId) {
        map.removeLayer(layer.id);
      }
    });
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    // Clear stored highlight data and remove zoom listener
    currentHighlightData = null;
    if (zoomUpdateTimeout) {
      clearTimeout(zoomUpdateTimeout);
      zoomUpdateTimeout = null;
    }
    map.off('zoom', MapUtils.updateHighlightOnZoom);
  },
  zoomToFeatureCollection: async (featureCollection: FeatureCollection) => {
    const map = window.map;
    if (!map) {
      return;
    }

    // Nhấp nháy layer
    const sourceId = 'blink';
    const fillLayerId = 'blink-fill';
    const lineLayerId = 'blink-line';
    const circleLayerId = 'blink-circle';

    if (map.getLayer(fillLayerId)) {
      map.removeLayer(fillLayerId);
    }
    if (map.getLayer(lineLayerId)) {
      map.removeLayer(lineLayerId);
    }
    if (map.getLayer(circleLayerId)) {
      map.removeLayer(circleLayerId);
    }
    if (map.getLayer(symbolLayerId)) {
      map.removeLayer(symbolLayerId);
    }
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    map.addSource(sourceId, {
      type: 'geojson',
      data: featureCollection as any,
    });

    const bbox = Turf.bbox(featureCollection);
    map.fitBounds(bbox as any, {
      duration: 500,
      padding: 50,
    });

    // Đợi 500ms
    await new Promise((resolve) => setTimeout(resolve, 500));
  },
  updateHighlightOnZoom: () => {
    if (!currentHighlightData) return;

    // Clear existing timeout
    if (zoomUpdateTimeout) {
      clearTimeout(zoomUpdateTimeout);
    }

    // Debounce zoom updates to prevent flickering during continuous zoom
    zoomUpdateTimeout = setTimeout(() => {
      if (currentHighlightData) {
        MapUtils.updateHighlightGeometry(currentHighlightData.featureCollection, currentHighlightData.elementStyle);
      }
    }, 100); // 100ms debounce
  },

  updateHighlightGeometry: (featureCollection: FeatureCollection, elementStyle?: { size?: number; frame?: string; strokeWidth?: number; opacity?: number }) => {
    const map = window.map;
    if (!map) return;

    // Only update geometry data, don't recreate layers to prevent flickering
    if (featureCollection.features.length === 1) {
      const feature = featureCollection.features[0];

      if (feature.geometry.type === 'LineString') {
        // Recalculate buffer for LineString with current zoom level
        const zoom = map.getZoom();
        const metersPerPixel = 156543.03392 * Math.cos(feature.geometry.coordinates[0][1] * Math.PI / 180) / Math.pow(2, zoom);
        const paddingPixels = 5;
        const bufferDistance = (paddingPixels * metersPerPixel) / 1000; // Convert to kilometers for Turf

        // Create buffer polygon around the LineString
        let bufferPolygon;
        try {
          bufferPolygon = Turf.buffer(feature, bufferDistance, { units: 'kilometers' });
        } catch (error) {
          console.error('Error creating buffer for LineString during zoom update:', error);
          // Fallback to bounding box if buffer fails
          const bbox = Turf.bbox(feature);
          bufferPolygon = Turf.bboxPolygon(bbox);
        }

        // Update existing source data instead of recreating
        const source = map.getSource(sourceId) as any;
        if (source) {
          source.setData(bufferPolygon);
        }
      }
      else if (feature.geometry.type === 'Polygon') {
        // Recalculate bounding box for Polygon with current zoom level
        const bbox = Turf.bbox(feature);
        const [minLng, minLat, maxLng, maxLat] = bbox;

        const southWest = map.project([minLng, minLat]);
        const northEast = map.project([maxLng, maxLat]);
        const pixelWidth = Math.abs(northEast.x - southWest.x);
        const pixelHeight = Math.abs(northEast.y - southWest.y);

        const paddingPixels = 20;
        const paddingLng = (paddingPixels / pixelWidth) * (maxLng - minLng);
        const paddingLat = (paddingPixels / pixelHeight) * (maxLat - minLat);

        const expandedBbox = [
          minLng - paddingLng,
          minLat - paddingLat,
          maxLng + paddingLng,
          maxLat + paddingLat
        ];

        const rectanglePolygon = Turf.bboxPolygon(expandedBbox);

        // Update existing source data instead of recreating
        const source = map.getSource(sourceId) as any;
        if (source) {
          source.setData(rectanglePolygon);
        }
      }
    } else {
      // Multiple features - update overall bounding box
      const overallBbox = Turf.bbox(featureCollection);
      const [minLng, minLat, maxLng, maxLat] = overallBbox;

      const southWest = map.project([minLng, minLat]);
      const northEast = map.project([maxLng, maxLat]);
      const pixelWidth = Math.abs(northEast.x - southWest.x);
      const pixelHeight = Math.abs(northEast.y - southWest.y);

      const paddingPixels = 20;
      const paddingLng = (paddingPixels / pixelWidth) * (maxLng - minLng);
      const paddingLat = (paddingPixels / pixelHeight) * (maxLat - minLat);

      const expandedBbox = [
        minLng - paddingLng,
        minLat - paddingLat,
        maxLng + paddingLng,
        maxLat + paddingLat
      ];

      const rectanglePolygon = Turf.bboxPolygon(expandedBbox);

      // Update existing source data
      const source = map.getSource(sourceId) as any;
      if (source) {
        source.setData(rectanglePolygon);
      }
    }
  },

  highlightFeatureCollection: async (featureCollection: FeatureCollection, elementStyle?: { size?: number; frame?: string; strokeWidth?: number; opacity?: number }) => {
    const map = window.map;
    if (!map) {
      return;
    }

    // Store highlight data for zoom updates
    currentHighlightData = { featureCollection, elementStyle };

    // Xóa layer cũ
    const layers = map.getStyle()?.layers || [];
    layers.forEach((layer: any) => {
      if (layer.source === sourceId) {
        map.removeLayer(layer.id);
      }
    });
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    // highlight single feature
    if (featureCollection.features.length === 1) {
      const feature = featureCollection.features[0];

      // Handle Point geometry with special icon highlight
      if (feature.geometry.type === 'Point') {
        // add a point with image is a square around the point
        map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: feature.geometry,
              },
            ],
          } as any,
        });
        const imageName = `dot_${UUIDUtils.generateUUID()}`;
        const defaultSize = 15;
        // Use element style size if provided, otherwise use default
        const elementSize = elementStyle?.size || 1;
        const baseIconSize = defaultSize * elementSize;
        const iconSize = baseIconSize * 2; // Highlight should be 2x larger than actual element

        // Use element frame style if provided, fallback to square if circle not available
        const highlightIcon = 'highlight_square'; // TODO: Add highlight_circle icon if needed
        const bitmap = await IconUtil.getBitmap(<Icon icon={highlightIcon} size={iconSize} fill={'red'} />, iconSize, 'none');
        window.map?.addImage(imageName, bitmap!)
        map.addLayer({
          id: 'dot',
          type: 'symbol',
          source: sourceId,
          layout: {
            'icon-image': imageName,
            'icon-size': 1,
            'icon-allow-overlap': true,
            'icon-anchor': 'center',
          },
        });
      }
      // Handle LineString with buffer polygon highlight
      else if (feature.geometry.type === 'LineString') {
        // Calculate buffer distance based on current zoom level for fixed pixel padding
        const zoom = map.getZoom();
        const metersPerPixel = 156543.03392 * Math.cos(feature.geometry.coordinates[0][1] * Math.PI / 180) / Math.pow(2, zoom);
        const paddingPixels = 5;
        const bufferDistance = (paddingPixels * metersPerPixel) / 1000; // Convert to kilometers for Turf

        // Create buffer polygon around the LineString
        let bufferPolygon;
        try {
          bufferPolygon = Turf.buffer(feature, bufferDistance, { units: 'kilometers' });
          console.log('MapUtils.highlightFeatureCollection - creating buffer for LineString', {
            zoom,
            metersPerPixel,
            bufferDistance,
            paddingPixels
          });
        } catch (error) {
          console.error('Error creating buffer for LineString:', error);
          // Fallback to bounding box if buffer fails
          const bbox = Turf.bbox(feature);
          bufferPolygon = Turf.bboxPolygon(bbox);
        }

        map.addSource(sourceId, {
          type: 'geojson',
          data: bufferPolygon as any,
        });

        // Add stroke layer for buffer outline
        map.addLayer({
          id: lineLayerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': 'red',
            'line-width': 1.5,
            'line-opacity': 0.7,
          },
        });
      }
      // Handle Polygon with bounding box highlight
      else if (feature.geometry.type === 'Polygon') {
        // Calculate bounding box of the feature
        const bbox = Turf.bbox(feature);
        const [minLng, minLat, maxLng, maxLat] = bbox;

        // Convert bbox to pixel coordinates to calculate size
        const southWest = map.project([minLng, minLat]);
        const northEast = map.project([maxLng, maxLat]);
        const pixelWidth = Math.abs(northEast.x - southWest.x);
        const pixelHeight = Math.abs(northEast.y - southWest.y);

        // Add fixed padding in pixels
        const paddingPixels = 20;

        // Convert back to map units for bounding box
        const paddingLng = (paddingPixels / pixelWidth) * (maxLng - minLng);
        const paddingLat = (paddingPixels / pixelHeight) * (maxLat - minLat);

        // Create expanded bounding box with padding
        const expandedBbox = [
          minLng - paddingLng,
          minLat - paddingLat,
          maxLng + paddingLng,
          maxLat + paddingLat
        ];

        // Create rectangle polygon for the bounding box
        const rectanglePolygon = Turf.bboxPolygon(expandedBbox);

        console.log('MapUtils.highlightFeatureCollection - creating bounding box for Polygon', {
          originalBbox: bbox,
          pixelSize: { width: pixelWidth, height: pixelHeight },
          expandedBbox
        });

        map.addSource(sourceId, {
          type: 'geojson',
          data: rectanglePolygon as any,
        });

        // Add stroke layer for bounding box outline
        map.addLayer({
          id: lineLayerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': 'red',
            'line-width': 1.5,
            'line-opacity': 0.7,
          },
        });
      }
    }
    // end highlight single feature
    // highlight multiple features
    else {
      // Calculate overall bounding box for all features
      const overallBbox = Turf.bbox(featureCollection);
      const [minLng, minLat, maxLng, maxLat] = overallBbox;

      // Convert bbox to pixel coordinates to calculate size
      const southWest = map.project([minLng, minLat]);
      const northEast = map.project([maxLng, maxLat]);
      const pixelWidth = Math.abs(northEast.x - southWest.x);
      const pixelHeight = Math.abs(northEast.y - southWest.y);

      // Add fixed padding in pixels
      const paddingPixels = 20;

      // Convert back to map units for bounding box
      const paddingLng = (paddingPixels / pixelWidth) * (maxLng - minLng);
      const paddingLat = (paddingPixels / pixelHeight) * (maxLat - minLat);

      // Create expanded bounding box with padding
      const expandedBbox = [
        minLng - paddingLng,
        minLat - paddingLat,
        maxLng + paddingLng,
        maxLat + paddingLat
      ];

      // Create rectangle polygon for the overall bounding box
      const rectanglePolygon = Turf.bboxPolygon(expandedBbox);

      console.log('MapUtils.highlightFeatureCollection - creating bounding box for multiple features', {
        featureCount: featureCollection.features.length,
        overallBbox,
        pixelSize: { width: pixelWidth, height: pixelHeight },
        expandedBbox
      });

      map.addSource(sourceId, {
        type: 'geojson',
        data: rectanglePolygon as any,
      });

      // Add stroke layer for bounding box outline
      map.addLayer({
        id: lineLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': 'red',
          'line-width': 3,
          'line-opacity': 0.8,
        },
      });
    }
    // end highlight multiple features

    // Add zoom event listener to update highlight box when zoom changes
    // Remove any existing listener first to avoid duplicates
    map.off('zoom', MapUtils.updateHighlightOnZoom);
    map.on('zoom', MapUtils.updateHighlightOnZoom);

  },
  /**
   * Trigger a resize event to update the map
   */
  triggerResizeEvent: () => {
    const map = window.map;
    const beforeMap = window.beforeMap;
    if (map) {
      map.resize();
    }
    if (beforeMap) {
      beforeMap.resize();
    }

    // Trigger window resize event for other components
    const resizeEvent = new Event('resize');
    window.dispatchEvent(resizeEvent);

    // Call resize on next frame after DOM reflow
    requestAnimationFrame(() => {
      if (window.map) {
        window.map.resize();
      }
      if (window.beforeMap) {
        window.beforeMap.resize();
      }
    });

    // Secondary timeout to catch slower animations or heavy component unmounting
    setTimeout(() => {
      if (window.map) {
        window.map.resize();
      }
      if (window.beforeMap) {
        window.beforeMap.resize();
      }
      window.dispatchEvent(new Event('resize'));
    }, 150);
  },

  /**
   * Zoom to default view - Thu phóng mặc định
   * hiện tại sẽ zoom tới bounds, nếu không có thì zoom tới Việt Nam
   * @param bounds: Nên truyền vào project bounds
   */
  zoomToDefaultView: (bounds?: LngLatBounds) => {
    const map = window.map;
    if (!map) {
      return;
    }
    const VietnamBounds = new LngLatBounds([
      [100.63813521986384, 8.180044265401762],
      [111.92864096015262, 23.492204558438758],
    ]);

    if (bounds) {
      map.fitBounds(bounds);
    } else {
      map.fitBounds(VietnamBounds);
    }
  },

  zoomIn: () => {
    const map = window.map;
    if (!map) {
      return;
    }
    map.zoomIn();
  },

  zoomOut: () => {
    const map = window.map;
    if (!map) {
      return;
    }
    map.zoomOut();
  },
};

export default MapUtils;
